import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC, RefObject, useRef, CSSProperties } from 'react';
import { InView } from 'react-intersection-observer';
import { CSSTransition } from 'react-transition-group';

import { MESSAGE_ID_ATTRIBUTE_NAME } from '@gem/utils';

import { MessageViewPageType } from 'APP/main/ChatView/Messages/Messages.types';
import { ChatMessage } from 'STORE/Messages/Message/ChatMessage/ChatMessage';

import {
  HIGHLIGHT_HOLD_ANIMATION_DURATION,
  HIGHLIGHT_IN_AND_OUT_ANIMATION_DURATION,
} from './Message.constants';
import { useMessagePresenter } from './Message.presenter';
import styles from './Message.styles.m.css';
import { MessageContextMenu } from './MessageContextMenu/MessageContextMenu';
import { Payload as PayloadComponent } from './Payload/Payload';

const CSSTransitionClasses = {
  enter: styles.focusMessage,
  enterActive: styles.focusMessageActive,
  exit: styles.exitFocusMessage,
  exitActive: styles.exitFocusMessageActive,
};

interface IMessageProps {
  message: ChatMessage;
  scrollRef: RefObject<HTMLDivElement>;
  messageViewPageType: MessageViewPageType;
}

export const Message: FC<IMessageProps> = observer(
  ({ message, scrollRef, messageViewPageType }) => {
    const messageContainerRef = useRef(null);

    const presenter = useMessagePresenter({ message, messageViewPageType });

    const messageClassName = classNames(styles.message, styles[`type-${presenter.payloadType}`], {
      [styles.topGap]: presenter.message.isLocalFirst,
    });

    const messageStyle = {
      '--highlight-in-and-out-animation-duration': `${HIGHLIGHT_IN_AND_OUT_ANIMATION_DURATION}ms`,
    } as CSSProperties;

    return (
      <CSSTransition
        in={presenter.hasAnimation}
        timeout={{
          enter: HIGHLIGHT_IN_AND_OUT_ANIMATION_DURATION + HIGHLIGHT_HOLD_ANIMATION_DURATION,
          exit: HIGHLIGHT_IN_AND_OUT_ANIMATION_DURATION,
        }}
        classNames={CSSTransitionClasses}
        onEntered={presenter.onFinishAnimation}
      >
        <div
          {...{ [MESSAGE_ID_ATTRIBUTE_NAME]: presenter.message.id }}
          className={messageClassName}
          style={messageStyle}
          ref={messageContainerRef}
          role="button"
          tabIndex={0}
          onDoubleClick={
            presenter.message.canBeReplied ? presenter.onMessageDoubleClick : undefined
          }
        >
          {presenter.hasViewObserver && (
            <InView
              key="viewObserver"
              className={styles.viewObserver}
              root={scrollRef.current}
              onChange={presenter.onView}
            />
          )}

          {presenter.hasPreviewObserver && (
            <InView
              key="preViewObserver"
              className={styles.preViewObserver}
              root={scrollRef.current}
              onChange={presenter.onPreview}
            />
          )}
          {presenter.hasContextMenu && (
            <MessageContextMenu
              reference={messageContainerRef}
              message={presenter.message}
              messageViewPageType={messageViewPageType}
            />
          )}

          <div
            className={classNames(styles.wrapper, {
              [styles.serviceMessage]: message.group?.isServiceMessages,
            })}
          >
            <PayloadComponent
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              common={presenter}
              messageViewPageType={messageViewPageType}
            />
          </div>
        </div>
      </CSSTransition>
    );
  }
);
